import { Button, EmailField, FetchingButton, FieldSet, email as emailSchema } from '@glade-ai/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { Controller, type UseFormProps, useForm } from 'react-hook-form';
import z from 'zod';

import getReturnToFromRouter from '@/components/Auth/helpers/getReturnToFromRouter';
import FieldError from '@/components/FieldError';
import deserializeQueryStringItem from '@/helpers/deserializeQueryStringItem';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import { getNextUrl } from '@/lib/Auth/useAfterAuth';
import { mixpanelAlias, mixpanelTrack } from '@/providers/Mixpanel';
import * as tsClient from '@/tsClient';
import { PasswordField } from '@/ui/exemplars/fields/PasswordField';

import { type View } from '../types';

type Props = {
  creatorSlug?: string;
  isCreator?: boolean;
  email?: string;
  onChangeView: (view: View, data: { email: string }) => void;
  hasFinishedOnboarding?: boolean;
};

// react-hook-form schema
const signInFormSchema = z.object({
  email: emailSchema(),
  password: z.string().min(1, { message: 'Please enter your password.' }),
});

type SignInFormSchema = z.infer<typeof signInFormSchema>;

const SignInWithEmail: React.FC<Props> = ({
  isCreator,
  creatorSlug,
  email: propsEmail,
  onChangeView,
  hasFinishedOnboarding,
}) => {
  const router = useRouter();
  const queryEmail = deserializeQueryStringItem(router.query.email, { type: 'string' });
  const [_user, _setUser, setUserByToken] = useUser();
  const providedEmail = useMemo(() => queryEmail || propsEmail, [queryEmail, propsEmail]);
  const returnTo = useMemo(
    () =>
      getReturnToFromRouter({
        returnToQuery: router.query.returnTo,
        routerAsPath: router.asPath,
        routerIsReady: router.isReady,
        routerPathname: router.pathname,
      }),
    [router.asPath, router.isReady, router.pathname, router.query.returnTo],
  );

  const formConfig = useMemo(
    () =>
      ({
        defaultValues: {
          email: queryEmail || propsEmail || '',
          password: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(signInFormSchema),
      }) satisfies UseFormProps<SignInFormSchema>,
    [propsEmail, queryEmail],
  );

  const { control, handleSubmit, setValue, watch } = useForm<SignInFormSchema>(formConfig);
  const email = watch('email');

  const {
    data: signInResponse,
    error: signInError,
    fetchingState: signInState,
    getData: passwordSignIn,
  } = useNoodleApi(tsClient.auth.passwordSignIn, {
    healthMonitor: { name: 'sign-in' },
  });

  useEffect(() => {
    if (signInResponse) {
      const { isExistingUser, token, primaryUserWorkflowId } = signInResponse;
      const currentUser = setUserByToken(token);

      if (currentUser) {
        const url = getNextUrl({
          creatorSlug,
          primaryUserWorkflowId: primaryUserWorkflowId || undefined,
          returnTo: returnTo || undefined,
          user: currentUser,
        });
        router.push(url);
      }

      mixpanelTrack('Logged In', {
        email,
        method: 'Email and password',
      });
      if (!isExistingUser) {
        mixpanelTrack('Created Account', {
          creationMethod: 'Normal Signup (Login)',
        });
        if (currentUser) {
          mixpanelAlias(currentUser.id);
        }
      }
    }
    // email only in mixpanelTrack, don't execute if it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInResponse, setUserByToken, router, creatorSlug]);

  useEffect(() => {
    if (providedEmail) {
      setValue('email', providedEmail, { shouldValidate: true });
    }
  }, [providedEmail, setValue]);

  useEffect(() => {
    if (signInError) {
      mixpanelTrack('Sign in with email error', { reason: signInError.message, type: signInError.type || 'Unknown' });
    }
  }, [signInError]);

  const handleForgotPasswordClick = (): void => {
    onChangeView('forgotPassword', { email });
  };

  const onSubmit = async (data: SignInFormSchema): Promise<void> => {
    const identifier = data.email || providedEmail;
    if (identifier) {
      await passwordSignIn({
        creatorSlug,
        hasFinishedOnboarding,
        identifier,
        isCreator: isCreator ?? false,
        password: data.password,
        returns: ['primaryUserWorkflowId'],
      });
    }
  };

  const errorMessage =
    (signInError?.type === 'LoginFailedError' &&
      (signInError?.data as { reason: string })?.reason === 'Wrong password' && (
        <>
          Wrong password. Try again or click{' '}
          <Button type='button' variant='link' onClick={() => onChangeView('forgotPassword', { email })}>
            Forgot password
          </Button>{' '}
          to reset it.
        </>
      )) ||
    (signInError?.type === 'LoginNoSuchUserError' && 'No user found.') ||
    signInError?.message ||
    null;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldSet disabled={signInState.isFetching}>
        <Controller
          name='email'
          control={control}
          rules={{ required: true }}
          disabled={!!providedEmail}
          render={({ field, fieldState }) => <EmailField field={field} fieldState={fieldState} />}
        />
        <Controller
          name='password'
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <PasswordField field={field} fieldState={fieldState} onForgotPasswordClick={handleForgotPasswordClick} />
          )}
        />
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
        <FetchingButton variant='primary' type='submit' size='lg' fetching={signInState.isFetching} className='w-full'>
          Sign In
        </FetchingButton>
      </FieldSet>
    </form>
  );
};

export default SignInWithEmail;
